import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Upload, X, Camera, BadgeCheck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const COMMON_INTERESTS = ["Travel", "Art", "Music", "Cooking", "Reading", "Wine", "Coffee", "Hiking", "Fitness", "Cinema", "Photography", "Yoga", "Fashion", "Tech", "Writing"];

export default function ProfileEdit() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const verifyRef = useRef(null);

  const [form, setForm] = useState({
    name: user?.name || "",
    age: 25,
    bio: "",
    location: "",
    occupation: "",
    interests: [],
    looking_for: "",
    photos: [],
    gender: "",
    interested_in: [],
    headline: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  const verifyStatus = user?.verification_status || "unverified";
  const isPremium = user?.subscription_tier === "premium";

  const submitVerification = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setVerifying(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/verification/submit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refresh();
      toast.success("Submitted. Our team will confirm shortly.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Verification couldn't be completed");
    } finally {
      setVerifying(false);
      if (verifyRef.current) verifyRef.current.value = "";
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profile");
        if (data) setForm({ ...form, ...data });
      } finally {
        setLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    })();
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleInterest = (t) => {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(t) ? f.interests.filter((x) => x !== t) : [...f.interests, t],
    }));
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Photo must be under 15MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/profile/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, photos: [...f.photos, data.path] }));
      toast.success("Photo uploaded");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && detail) {
        toast.error(detail);
      } else if (err?.response?.status === 401) {
        toast.error("Please sign in again to upload photos.");
      } else if (err?.response?.status === 413) {
        toast.error("Photo is too large. Please choose a smaller image.");
      } else if (!err?.response) {
        toast.error("Network error. Check your connection and try again.");
      } else {
        toast.error("Upload failed. Please try a different photo.");
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = (path) => {
    setForm((f) => ({ ...f, photos: f.photos.filter((p) => p !== path) }));
  };

  const save = async () => {
    if (!form.name || !form.age || !form.bio || !form.location) {
      toast.error("Please fill in name, age, bio, and location");
      return;
    }
    setSaving(true);
    try {
      await api.put("/profile", {
        ...form,
        age: parseInt(form.age, 10),
      });
      await refresh();
      toast.success("Profile saved");
      navigate("/discover");
    } catch (err) {
      toast.error("Couldn't save profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-12 text-center text-gold-soft tracking-overline text-xs">LOADING</div>;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10 md:py-16" data-testid="profile-edit-page">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <div className="text-gold-soft tracking-overline text-xs mb-2">YOUR PROFILE</div>
        <h1 className="font-display text-4xl sm:text-5xl font-light mb-3">Tell us your story.</h1>
        <p className="text-white/55 leading-relaxed mb-12 max-w-xl">
          Take your time. A thoughtful profile is the start of a thoughtful connection.
        </p>

        <div className="glass-strong rounded-3xl p-8 md:p-10 space-y-10">
          {/* Verification */}
          <section data-testid="verification-section">
            <div className="text-xs tracking-overline text-white/40 mb-4">VERIFICATION</div>
            {verifyStatus === "verified" ? (
              <div className="flex items-start gap-4 p-5 rounded-2xl border border-[#D4AF37]/30 bg-[#D4AF37]/5">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center shrink-0">
                  <BadgeCheck className="text-gold" size={20} fill="rgba(212,175,55,0.3)" />
                </div>
                <div>
                  <div className="font-medium text-white" data-testid="verified-state">You're verified.</div>
                  <div className="text-sm text-white/55 leading-relaxed mt-1">
                    A small thing that means a lot — everyone you match with is a real, verified person, too.
                  </div>
                </div>
              </div>
            ) : verifyStatus === "pending_review" ? (
              <div className="flex items-start gap-4 p-5 rounded-2xl border border-white/15 bg-white/[0.02]" data-testid="pending-state">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center shrink-0">
                  <ShieldCheck className="text-gold-soft" size={20} />
                </div>
                <div>
                  <div className="font-medium text-white">Awaiting review.</div>
                  <div className="text-sm text-white/55 leading-relaxed mt-1">
                    Thank you. Our team usually reviews submissions within 24 hours.
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row items-start gap-5 p-5 rounded-2xl border border-white/15">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center shrink-0">
                  <ShieldCheck className="text-gold-soft" size={20} />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-white">Verify you're a real person.</div>
                  <div className="text-sm text-white/55 leading-relaxed mt-1 mb-4">
                    Take a quick selfie. Every Wanted member is verified — it's how we keep the community real.
                  </div>
                  <button
                    onClick={() => verifyRef.current?.click()}
                    disabled={verifying}
                    className="btn-outline-gold text-sm"
                    data-testid="verify-btn"
                  >
                    {verifying ? "Submitting..." : "Take verification selfie"}
                  </button>
                  <input
                    ref={verifyRef}
                    type="file"
                    accept="image/*"
                    capture="user"
                    onChange={submitVerification}
                    className="hidden"
                    data-testid="verify-file-input"
                  />
                </div>
              </div>
            )}
          </section>

          {/* Photos */}
          <section>
            <div className="flex items-baseline justify-between mb-4">
              <div className="text-xs tracking-overline text-white/40">PHOTOS</div>
              <div className="text-[10px] tracking-wide text-white/30">Drag to reorder · first photo is your main</div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {form.photos.map((p, idx) => (
                <div
                  key={p}
                  className={`relative aspect-square rounded-2xl overflow-hidden group cursor-move ${
                    dragIndex === idx ? "opacity-50" : ""
                  } ${idx === 0 ? "ring-2 ring-[#D4AF37]/50" : ""}`}
                  draggable
                  onDragStart={() => setDragIndex(idx)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragIndex === null || dragIndex === idx) return;
                    const next = [...form.photos];
                    const [moved] = next.splice(dragIndex, 1);
                    next.splice(idx, 0, moved);
                    setForm((f) => ({ ...f, photos: next }));
                    setDragIndex(null);
                  }}
                  onDragEnd={() => setDragIndex(null)}
                  data-testid={`photo-${idx}`}
                >
                  <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                  {idx === 0 && (
                    <div className="absolute top-2 left-2 text-[10px] tracking-overline text-[#050505] bg-[#D4AF37] px-2 py-0.5 rounded-full">
                      MAIN
                    </div>
                  )}
                  <button
                    onClick={() => removePhoto(p)}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid="remove-photo-btn"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              {form.photos.length < 6 && (
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="aspect-square rounded-2xl border border-dashed border-white/20 hover:border-[#D4AF37]/50 flex flex-col items-center justify-center gap-2 text-white/40 hover:text-gold transition-colors"
                  data-testid="add-photo-btn"
                >
                  {uploading ? (
                    <span className="text-xs tracking-overline">UPLOADING</span>
                  ) : (
                    <>
                      <Camera size={22} />
                      <span className="text-xs tracking-wide">Add photo</span>
                    </>
                  )}
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*,.heic,.heif" onChange={handlePhoto} className="hidden" data-testid="photo-file-input" />
            </div>
            <p className="text-xs text-white/40 mt-3">Up to 6 photos. Up to 15MB each. JPG, PNG, HEIC, WEBP, GIF.</p>
          </section>

          {/* Basic info */}
          <section className="space-y-4">
            <div className="text-xs tracking-overline text-white/40">BASICS</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input className="input-luxe" placeholder="Name" value={form.name} onChange={update("name")} data-testid="profile-name-input" />
              <input className="input-luxe" type="number" placeholder="Age" min="18" max="99" value={form.age} onChange={update("age")} data-testid="profile-age-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input className="input-luxe" placeholder="Location (e.g. New York, NY)" value={form.location} onChange={update("location")} data-testid="profile-location-input" />
              <input className="input-luxe" placeholder="Occupation (optional)" value={form.occupation} onChange={update("occupation")} data-testid="profile-occupation-input" />
            </div>
          </section>

          {/* Identity */}
          <section>
            <div className="text-xs tracking-overline text-white/40 mb-4">YOU</div>
            <div className="text-sm text-white/55 mb-3">I am a...</div>
            <div className="flex flex-wrap gap-2 mb-6">
              {[
                { v: "woman", l: "Woman" },
                { v: "man", l: "Man" },
                { v: "non-binary", l: "Non-binary" },
                { v: "other", l: "Other" },
              ].map((g) => (
                <button
                  key={g.v}
                  onClick={() => setForm({ ...form, gender: g.v })}
                  className={`px-4 py-2 rounded-full text-sm border transition-all ${
                    form.gender === g.v ? "border-[#D4AF37] text-gold bg-[#D4AF37]/10" : "border-white/15 text-white/70 hover:border-white/30"
                  }`}
                  data-testid={`gender-${g.v}`}
                >
                  {g.l}
                </button>
              ))}
            </div>
            <div className="text-sm text-white/55 mb-3">Open to meeting...</div>
            <div className="flex flex-wrap gap-2">
              {[
                { v: "woman", l: "Women" },
                { v: "man", l: "Men" },
                { v: "non-binary", l: "Non-binary" },
              ].map((g) => {
                const on = form.interested_in.includes(g.v);
                return (
                  <button
                    key={g.v}
                    onClick={() => setForm({
                      ...form,
                      interested_in: on
                        ? form.interested_in.filter((x) => x !== g.v)
                        : [...form.interested_in, g.v],
                    })}
                    className={`px-4 py-2 rounded-full text-sm border transition-all ${
                      on ? "border-[#D4AF37] text-gold bg-[#D4AF37]/10" : "border-white/15 text-white/70 hover:border-white/30"
                    }`}
                    data-testid={`interested-${g.v}`}
                  >
                    {g.l}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Bio */}
          <section>
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-xs tracking-overline text-white/40">ABOUT YOU</div>
              {isPremium && <span className="text-[10px] tracking-overline text-gold">PREMIUM</span>}
            </div>
            {isPremium && (
              <input
                className="input-luxe mb-3"
                placeholder="A short headline (e.g. 'Quiet weekends, loud opinions about pasta')"
                value={form.headline}
                onChange={update("headline")}
                data-testid="profile-headline-input"
              />
            )}
            <textarea
              rows={4}
              className="input-luxe resize-none"
              placeholder="A short, honest paragraph about who you are..."
              value={form.bio}
              onChange={update("bio")}
              data-testid="profile-bio-input"
            />
            <input
              className="input-luxe mt-3"
              placeholder="What you're looking for (optional)"
              value={form.looking_for}
              onChange={update("looking_for")}
              data-testid="profile-looking-input"
            />
          </section>

          {/* Interests */}
          <section>
            <div className="text-xs tracking-overline text-white/40 mb-4">INTERESTS</div>
            <div className="flex flex-wrap gap-2">
              {COMMON_INTERESTS.map((t) => {
                const on = form.interests.includes(t);
                return (
                  <button
                    key={t}
                    onClick={() => toggleInterest(t)}
                    className={`px-4 py-2 rounded-full text-sm border transition-all ${
                      on ? "border-[#D4AF37] text-gold bg-[#D4AF37]/10" : "border-white/15 text-white/70 hover:border-white/30"
                    }`}
                    data-testid={`interest-${t.toLowerCase()}`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </section>

          <div className="pt-4 flex flex-col sm:flex-row gap-3 justify-end">
            <button onClick={() => navigate("/discover")} className="btn-outline-gold" data-testid="profile-cancel-btn">
              Cancel
            </button>
            <button onClick={save} disabled={saving} className="btn-gold" data-testid="profile-save-btn">
              {saving ? "Saving..." : "Save profile"}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
